/**
 * Required or optional, per selected activity.
 *
 * An event declares what the group might do. The organizer decides which of
 * those the plan must deliver: a required activity is a hard constraint on every
 * candidate district and on the time window, while an optional one only lowers
 * the coverage score when the district lacks it, weighted by how many members
 * asked for it. Keeping the choice explicit on its own row is what stops a wish
 * from vetoing every district.
 *
 * The component is presentational: both the creation wizard and the event's own
 * activity page own the draft they edit, so there is one implementation of the
 * control and one place that decides what saving means.
 */

import { useTranslation } from 'react-i18next'

import { ACTIVITIES, type ActivityKey } from '@shared/activities'

import { cn, activityLabelKey } from '../../lib/format'

export interface ActivityRequirementPickerProps {
  /** Activities the event declares, in catalog order. */
  activities: readonly ActivityKey[]
  /** The subset that is only a wish. */
  optional: readonly ActivityKey[]
  onChange: (optional: ActivityKey[]) => void
  disabled?: boolean
}

export function ActivityRequirementPicker({
  activities,
  optional,
  onChange,
  disabled,
}: ActivityRequirementPickerProps) {
  const { t } = useTranslation()

  if (activities.length === 0) {
    return null
  }

  function setRequirement(activity: ActivityKey, isOptional: boolean): void {
    const current = new Set(optional)
    if (isOptional) {
      current.add(activity)
    } else {
      current.delete(activity)
    }
    onChange(activities.filter((key) => current.has(key)))
  }

  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-medium">{t('events.requirementTitle')}</span>
      <p className="text-muted-foreground text-xs">{t('events.requirementHint')}</p>
      <ul className="flex flex-col gap-1.5">
        {activities.map((activity) => {
          const isOptional = optional.includes(activity)
          return (
            <li key={activity} className="flex flex-wrap items-center gap-2">
              <span className="w-28 text-sm">
                <span aria-hidden className="mr-1">
                  {ACTIVITIES[activity].icon}
                </span>
                {t(activityLabelKey(activity))}
              </span>
              {([false, true] as const).map((value) => (
                <button
                  key={String(value)}
                  type="button"
                  aria-pressed={isOptional === value}
                  disabled={disabled}
                  className={cn(
                    'rounded-full border px-2.5 py-1 text-xs transition-colors',
                    'focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none',
                    isOptional === value
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border hover:bg-accent',
                  )}
                  onClick={() => setRequirement(activity, value)}
                >
                  {value ? t('events.requirementOptional') : t('events.requirementRequired')}
                </button>
              ))}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
